import React, { useRef, useState, useEffect } from "react";

const ReactVideoPlayer = ({ videoSrc, customStyles, showControls }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isDragging, setIsDragging] = useState(false); // Track dragging state
  const [startX, setStartX] = useState(0); // Store starting X position
  const [startTime, setStartTime] = useState(0); // Store video time at drag start

  useEffect(() => {
    const videoElement = videoRef.current;

    if (videoElement) {
      videoElement.addEventListener("waiting", handleBuffering);
      videoElement.addEventListener("playing", () => setIsBuffering(false));

      return () => {
        videoElement.removeEventListener("waiting", handleBuffering);
        videoElement.removeEventListener("playing", () => setIsBuffering(false));
      };
    }
  }, []);

  const togglePlayPause = () => {
    const videoElement = videoRef.current;

    if (videoElement) {
      if (!videoElement.paused) {
        videoElement.pause();
        setIsPlaying(false);
      } else {
        videoElement.play();
        setIsPlaying(true);
      }
    }
  };

  const getMimeTypeFromUrl = (url) => {
    const extensionToMime = {
        mp4: "video/mp4",
        webm: "video/webm",
        mov: "video/mp4",
        mkv: "video/x-matroska",
        avi: "video/x-msvideo",
        m4v: "video/x-m4v",
        flv: "video/x-flv",
        ogg: "video/ogg",
    };
    const extension = url.split(".").pop().toLowerCase();
    return extensionToMime[extension] || "application/octet-stream";
  };

  const handleBuffering = () => {
    const videoElement = videoRef.current;
    setIsBuffering(videoElement && videoElement.readyState < 3); // ReadyState less than 3 means loading
  };

  const handleDragStart = (event) => {
    event.preventDefault();
    const videoElement = videoRef.current;

    if (videoElement) {
      setIsDragging(true);
      setStartX(event.clientX || event.touches[0].clientX);
      setStartTime(videoElement.currentTime); // Record the current time at drag start
    }
  };

  const handleDragMove = (event) => {
    if (isDragging) {
      const videoElement = videoRef.current;
      if (videoElement) {
        const currentX = event.clientX || event.touches[0].clientX;
        const deltaX = currentX - startX;
        const duration = videoElement.duration;

        // Calculate the time change based on drag distance
        const timeChange = (deltaX / videoElement.offsetWidth) * duration;
        let newTime = Math.min(Math.max(startTime + timeChange, 0), duration);

        videoElement.currentTime = newTime; // Update video time
      }
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false); // Stop dragging
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        ...customStyles, // Apply custom styles
      }}
      onMouseDown={handleDragStart}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onTouchStart={handleDragStart}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
    >
      <video
        ref={videoRef}
        controls={showControls}
        controlsList="nodownload"
        width="100%"
        height="100%"
        onPlay={() => setIsPlaying(true)} // Sync with built-in controls
        onPause={() => setIsPlaying(false)}
      >
        <source src={videoSrc} type={getMimeTypeFromUrl(videoSrc)} />
        Your browser does not support the video tag.
      </video>

      {/* Custom Play/Pause Overlay */}
      <div
        onClick={togglePlayPause}
        style={{
          display: isPlaying ? "none" : "none", // Show only when paused
        }}
        className="videoPlayIcon"
      >
        <i className="fa-solid fa-play play-icon" aria-hidden="true"></i>
      </div>

      {/* Buffering Indicator */}
      {isBuffering && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 3,
            fontSize: "18px",
            color: "white",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            borderRadius: "5px",
            padding: "5px 10px",
          }}
        >
          Buffering...
        </div>
      )}
    </div>
  );
};

export default ReactVideoPlayer;